/**
 * Decision 0059: `RecordPrState` (the PR watch's snapshots and the merge results, relayed by the hub) and `requestMerge`
 * (a person chose Merge on the human review card). Nothing here creates an item: every reaction records, wakes, or
 * moves the item's status.
 */
import { MERGE_ORDER_TTL_MS, type MergeReport, type PrSnapshot, parseGithubPull } from "@minga/protocol"
import {
  isOwnPr,
  keepKnownMergeable,
  mergeablePrOf,
  mergeSentence,
  movedHeadOf,
  prContext,
  prFacts,
  prSnapshotHash,
  RUNNER_SIDE_DETAILS,
  reviewedShaOf,
  sendBackFact,
  stripExcerpts,
} from "../github"
import { deriveId } from "../hash"
import { isWorkingMember } from "../multiplayer"
import {
  advanceToHumanByMerge,
  closeByMerge,
  completeByMerge,
  failStage,
  openHumanReview,
  refreshHumanReview,
  reviewIntentKey,
  withdrawReviewCards,
} from "../runs"
import { artifactOf, itemOf, must } from "../state"
import type { Artifact, Command, Ids, Item, Proposal } from "../types"
import { forbidden, invalid, notFound, reject, type Tx } from "./tx"

type Cmd<T extends Command["_tag"]> = Extract<Command, { _tag: T }>

/** The item in review at its human stage (the only place a PR card is decided). */
const atHumanStage = (t: Item | undefined): t is Item =>
  t?.status === "in_review" && t.review.stages[t.review.stage] === "human"

export const recordPrState = (tx: Tx, c: Cmd<"RecordPrState">): void => {
  const actor = tx.actor
  const expired = c.merge?.result._tag === "expired"
  if (actor._tag === "system") {
    // decision 0069: the GitHub App path relays a re-read snapshot, never a merge report (S1 cannot merge)
    if (actor.process === "github_app") {
      if (c.snapshot === null || c.merge !== undefined)
        forbidden("actor_kind", "the GitHub App only relays a snapshot, never a merge report")
      if (c.snapshot.observedBy !== "github_app")
        invalid("a GitHub App snapshot is observed by github_app", "snapshot.observedBy")
    } else if (actor.process !== "scheduler" || c.snapshot !== null || !expired)
      forbidden("actor_kind", "the scheduler only reports an expired merge, with no snapshot")
  }
  if (actor._tag === "runner" && expired) forbidden("actor_kind", "a runner never reports an expired merge")
  if (actor._tag === "runner" && c.snapshot?.observedBy === "github_app")
    invalid("a runner's snapshot is observed by that runner", "snapshot.observedBy")
  const a = artifactOf(tx.state, c.artifactId) ?? notFound({ _tag: "artifact", id: c.artifactId })
  if (a.kind !== "pull_request") invalid("the artifact is not a pull request", "artifactId")
  const pull = parseGithubPull(a.uri)
  if (!pull) invalid("the artifact is not a github.com pull request", "artifactId")
  if (c.snapshot !== null) {
    const snap = c.snapshot
    if (snap.artifactId !== c.artifactId)
      invalid("the snapshot names another artifact", "snapshot.artifactId")
    if (
      snap.url !== pull.url ||
      snap.repo.toLowerCase() !== pull.repo.toLowerCase() ||
      snap.number !== pull.number
    )
      invalid("the snapshot is about another pull request", "snapshot.url")
  }
  if (c.merge && c.merge.artifactId !== c.artifactId)
    invalid("the merge report names another artifact", "merge")
  // a report counts only for the artifact's current Minga merge while it is in flight; a late one is ignored (its
  // snapshot still applies: the truth wins)
  const live = (r: MergeReport | undefined): MergeReport | undefined => {
    const m = artifactOf(tx.state, c.artifactId)?.merge
    return r && m?.mergeId === r.mergeId && (m.state === "merging" || m.state === "queued") ? r : undefined
  }
  if (c.snapshot !== null) applySnapshot(tx, a, c.snapshot, live(c.merge))
  const report = live(c.merge)
  if (report) applyMergeResult(tx, c.artifactId, report, runnerLabel(c.runnerName))
}

/**
 * Decision 0067 (system:scheduler): after a hydration put back the runs a PR needs (a state an older compaction
 * stripped), each named item at its human stage gets its card recomputed (`refreshHumanReview`: it gains or loses
 * Merge, and is bound). Idempotent: a card that is still right, an item elsewhere or gone, writes nothing.
 */
export const refreshReviewCards = (tx: Tx, c: Cmd<"RefreshReviewCards">): void => {
  if (tx.actor._tag !== "system") forbidden("actor_kind", "only the scheduler refreshes review cards")
  for (const id of new Set(c.itemIds)) if (atHumanStage(itemOf(tx.state, id))) refreshHumanReview(tx, id)
}

/** The hub's name of the runner, as the sentences use it (one line, cut); undefined says "the runner". */
const runnerLabel = (name: string | undefined): string | undefined => {
  const n = name?.replace(/\s+/g, " ").trim().slice(0, 60)
  return n ? n : undefined
}

/** §5.1–§5.3: dedupe, order, facts, transitions. Writes nothing when nothing material changed. */
const applySnapshot = (tx: Tx, a: Artifact, wire: PrSnapshot, report: MergeReport | undefined): void => {
  const prevView = a.pr
  const prev = prevView?.snapshot ?? null
  // an older snapshot, or an open one after a merge (a merge is final), is ignored
  if (
    prev !== null &&
    (wire.updatedAt < prev.updatedAt || (prev.state === "merged" && wire.state === "open"))
  )
    return
  const next = keepKnownMergeable(prev, wire)
  const hash = prSnapshotHash(next)
  // `ownRepo` is the hub's stamp, outside the material hash (the runner cannot know it): a flip alone still counts
  if (prevView !== undefined && hash === prevView.hash && prev?.ownRepo === next.ownRepo) return
  const ctx = prContext(tx.state, a, tx.now, report)
  const told = new Set(prevView?.told ?? [])
  const facts = prFacts(prev, next, ctx).filter((f) => !told.has(f.key))
  tx.emit({
    _tag: "PrStateObserved",
    artifactId: a.id,
    itemId: a.itemId,
    snapshot: stripExcerpts(next),
    hash: hash as never,
    facts,
  })
  const merged = facts.find((f) => f._tag === "merged")
  const closed = facts.find((f) => f._tag === "closed")
  const t = a.itemId ? itemOf(tx.state, a.itemId) : undefined
  // only the item's own PR moves the item (a link to another repo's or branch's PR is recorded, shown and wakes, never
  // more): otherwise any posted PR link, merged or closed by anyone, would pass or block the item's review
  const own = isOwnPr(tx.state, a, next)
  if (merged?._tag === "merged") {
    const cur = must(artifactOf(tx.state, a.id), "artifact")
    if (cur.state !== "published")
      tx.emit({ _tag: "ArtifactStateChanged", artifactId: a.id, state: "published", before: cur.state })
    // a merge passes review stages only when it happened after the PR was posted to Minga (a first snapshot that is
    // already merged from before is recorded; the reviews go on)
    const fresh = prev !== null || (next.mergedAt !== null && next.mergedAt >= a.createdAt)
    // A merged PR can be a step toward deployment/publication, rather than the publish item's outcome.
    if (own && fresh && t?.status === "in_review" && t.kind !== "publish") {
      const facts = { url: next.url, number: next.number, sha: next.mergeSha, login: next.mergedBy }
      if (merged.via === "run") advanceToHumanByMerge(tx, t.id, facts)
      else
        completeByMerge(tx, t.id, {
          ...facts,
          via: merged.via,
          ...(merged.via === "minga" && ctx.minga ? { by: ctx.minga.by } : {}),
        })
    }
    // decision 0156 (REP-66): a merge ends the review loop of an item blocked after its rounds or back in todo for a
    // fix: it is done (merged), never "blocked, review rounds exhausted" over code that has landed
    else if (own && fresh && t && (t.status === "blocked" || t.status === "todo") && t.review.round > 0)
      closeByMerge(tx, t.id)
    return
  }
  if (closed) {
    const cur = must(artifactOf(tx.state, a.id), "artifact")
    if (cur.state !== "rejected")
      tx.emit({ _tag: "ArtifactStateChanged", artifactId: a.id, state: "rejected", before: cur.state })
    const m = cur.merge
    if (m && (m.state === "merging" || m.state === "queued")) {
      const result = { _tag: "refused" as const, reason: "closed" as const, detail: null }
      tx.emit({
        _tag: "MergeFinished",
        mergeId: m.mergeId,
        artifactId: a.id,
        itemId: a.itemId,
        result,
        sentence: mergeSentence(result, next),
      })
    }
    if (own && t?.status === "in_review") {
      withdrawReviewCards(tx, t.id)
      tx.emit({
        _tag: "ItemStatusChanged",
        itemId: t.id,
        from: "in_review",
        to: "blocked",
        cause: { _tag: "pr_closed", url: next.url, number: next.number },
        blocked: {
          _tag: "reported",
          blocker: {
            _tag: "other",
            what: `PR #${next.number} was closed on GitHub without merging. Start a new attempt (a new PR), or cancel the item, and say why.`,
          },
          runId: t.lastRunId,
        },
        resolution: null,
      })
    }
    return
  }
  // changes requested on GitHub by a writer, at the human stage, on the current head: a person round (decision 0050)
  const back = own ? sendBackFact(facts, next, ctx) : undefined
  if (back && atHumanStage(t)) {
    const line = `Changes requested on GitHub by ${back.login}: ${back.url}`
    withdrawReviewCards(tx, t.id)
    tx.emit({
      _tag: "ReviewRecorded",
      itemId: t.id,
      round: t.review.round,
      stage: "human",
      verdict: "changes_requested",
      notes: line,
      findings: [line],
      runId: null,
    })
    failStage(tx, t.id, "changes_requested", null)
    return
  }
  // a first snapshot, or a change in what Merge may do (the PR's state, its `ownRepo` stamp, its ownership; decision
  // 0067): the card follows (it gains or loses Merge, and is bound). `refreshHumanReview` writes nothing while the
  // card's options are still right, so it is asked on every observed change.
  if (atHumanStage(t)) refreshHumanReview(tx, t.id)
}

/** §5.6: the merge part. `report` names the artifact's in-flight Minga merge. */
const applyMergeResult = (
  tx: Tx,
  artifactId: Ids["ArtifactId"],
  report: MergeReport,
  runnerName: string | undefined,
): void => {
  const a = must(artifactOf(tx.state, artifactId), "artifact")
  const m = must(a.merge, "merge")
  const snap = a.pr?.snapshot ?? null
  const r = report.result
  const runnerSide =
    r._tag === "refused" &&
    r.reason === "other" &&
    RUNNER_SIDE_DETAILS.some((d) => (r.detail ?? "").startsWith(d))
  const sentence = mergeSentence(r, snap, {
    reviewedSha: m.headSha,
    runnerSide,
    ...(runnerName !== undefined ? { runnerName } : {}),
  })
  tx.emit({
    _tag: "MergeFinished",
    mergeId: m.mergeId,
    artifactId,
    itemId: a.itemId,
    result: r,
    sentence,
  })
  if (r._tag !== "refused" && r._tag !== "expired") return
  // the card re-opens with the sentence on top (same round, same intentKey), still bound to the SHA the person reviewed
  // (the order's): a head that moved meanwhile is nobody's review, and a later Merge on it is refused as sha_moved.
  // Decision 0070 (TRI-18, amends 0059 rule 4): a runner's `sha_moved` re-binds the card to the head it reported, as
  // the cloud-side refusal does, so the sentence's "Merge again merges <new>" is true
  const t = a.itemId ? itemOf(tx.state, a.itemId) : undefined
  if (!atHumanStage(t)) return
  withdrawReviewCards(tx, t.id)
  const moved =
    r._tag === "refused" && r.reason === "sha_moved" ? movedHeadOf(r.detail, snap, m.headSha) : null
  const source = a.pr?.reviewed?.sha === m.headSha ? a.pr.reviewed.source : "snapshot"
  openHumanReview(tx, t, {
    ...(sentence !== null ? { notice: sentence } : {}),
    bind: moved !== null ? { sha: moved, source: "snapshot" } : { sha: m.headSha, source },
  })
}

/** The notice on a review card re-opened after a Merge with no open PR of the item to merge (decision 0067). */
export const NO_PR_TO_MERGE =
  "There is no open pull request of this item to merge from Minga any more: approve without merging, or request changes."

/**
 * §5.5: a person chose Merge on the item's human review card. The card stays open ("Merging…") until the result. A PR
 * that moved since the card's bound SHA is refused at once (no order) and the card re-opens bound to the new head.
 */
export const requestMerge = (tx: Tx, p: Proposal): void => {
  const body = p.body
  if (body._tag !== "question" || body.subject?._tag !== "item") invalid("not a review card", "proposalId")
  const t = itemOf(tx.state, body.subject.id)
  if (!t || p.intentKey !== reviewIntentKey(t.id, t.review.round) || !atHumanStage(t))
    invalid("this review card is out of date", "decision.optionKey")
  const decider = tx.actor
  if (decider._tag !== "user") forbidden("actor_kind", "only a person merges")
  const a = mergeablePrOf(tx.state, t)
  if (!a?.pr) {
    // decision 0067: a card that offers Merge with no PR of the item to merge is stale (the PR was closed or
    // superseded, or a compacted state lost its posting run): no order, the card re-opens with what it can do now
    withdrawReviewCards(tx, t.id)
    openHumanReview(tx, t, { notice: NO_PR_TO_MERGE })
    return
  }
  // wave E (decision 0063, answer 5): any project member merges; it still runs with the watcher's gh, and the
  // result line says for whom ("Merged by Kate for Becky")
  if (!isWorkingMember(tx.state, decider.userId)) forbidden("not_owner", "only a project member merges")
  if (a.merge?.state === "merging" || a.merge?.state === "queued")
    reject({ _tag: "Duplicate", existing: { _tag: "artifact", id: a.id }, reason: "already merging" })
  // a card opened before this wave has no binding: bind it now, from the same sources
  let reviewed = a.pr.reviewed?.proposalId === p.id ? a.pr.reviewed : undefined
  if (!reviewed) {
    const b = reviewedShaOf(tx.state, a)
    if (!b) invalid("there is no reviewed head to merge", "decision.optionKey")
    tx.emit({
      _tag: "PrReviewBound",
      artifactId: a.id,
      itemId: t.id,
      proposalId: p.id,
      sha: b.sha,
      source: b.source,
    })
    reviewed = { proposalId: p.id, sha: b.sha, source: b.source }
  }
  const seq = tx.state.seq
  const mergeId = deriveId<Ids["MergeId"]>("mrg", tx.now, `merge:${p.id}:${seq}`)
  const head = a.pr.snapshot.headSha
  if (head !== reviewed.sha) {
    // "you never approve something that changed under you": no order, the card re-opens bound to the new head
    const result = { _tag: "refused" as const, reason: "sha_moved" as const, detail: head }
    const sentence = mergeSentence(result, a.pr.snapshot, { reviewedSha: reviewed.sha })
    tx.emit({
      _tag: "MergeFinished",
      mergeId,
      artifactId: a.id,
      itemId: t.id,
      result,
      sentence,
      by: decider.userId,
    })
    withdrawReviewCards(tx, t.id)
    openHumanReview(tx, must(itemOf(tx.state, t.id), "item"), {
      ...(sentence !== null ? { notice: sentence } : {}),
      bindToHead: true,
    })
    return
  }
  tx.emit({
    _tag: "MergeRequested",
    mergeId,
    artifactId: a.id,
    itemId: t.id,
    proposalId: p.id,
    headSha: reviewed.sha,
    by: decider.userId,
    notAfter: tx.now + MERGE_ORDER_TTL_MS,
  })
}
